/**
 * 立寄り順 (集荷 + 経由地) の並び替え計算 (#723)。
 *
 * DraftOrder は経由地に訪問順の数値を持たせず、「`waypoints` 配列の並び + `pickup_sequence`」で
 * 訪問順を表す。経由地を集荷より前にも後にも置けることが要点で、
 * パターンB (自社倉庫で積込 → 取引先の集荷先でピック → 納品) を表現するために必要 (#721)。
 * 納品は常に最後なのでスロットに含めない。
 */

export type VisitSlot = { type: "pickup" } | { type: "waypoint"; index: number };

/** 集荷の訪問順 (1 始まり) を 1..経由地件数+1 に収める。取込・古いデータへの防御。 */
export function clampPickupSequence(pickupSequence: number, waypointCount: number): number {
  const max = waypointCount + 1;
  if (!Number.isFinite(pickupSequence)) return 1;
  return Math.min(Math.max(Math.trunc(pickupSequence), 1), max);
}

/** 集荷の訪問順と経由地の件数から、訪問順に並んだスロット列を作る。 */
export function buildVisitSlots(pickupSequence: number, waypointCount: number): VisitSlot[] {
  const pickupAt = clampPickupSequence(pickupSequence, waypointCount);
  const slots: VisitSlot[] = [];
  let waypointIndex = 0;
  for (let position = 1; position <= waypointCount + 1; position++) {
    if (position === pickupAt) {
      slots.push({ type: "pickup" });
      continue;
    }
    slots.push({ type: "waypoint", index: waypointIndex });
    waypointIndex++;
  }
  return slots;
}

/**
 * スロット列から「集荷の訪問順」と「経由地の並び替え後の順序」を取り出す。
 * `waypointOrder[i]` は並び替え後 i 番目に来る経由地の、元の配列インデックス。
 */
export function readVisitSlots(slots: VisitSlot[]): {
  pickupSequence: number;
  waypointOrder: number[];
} {
  const pickupAt = slots.findIndex((slot) => slot.type === "pickup");
  return {
    pickupSequence: pickupAt < 0 ? 1 : pickupAt + 1,
    waypointOrder: slots.flatMap((slot) => (slot.type === "waypoint" ? [slot.index] : [])),
  };
}

/**
 * 立寄り順のドラッグ移動を計算する。
 * `from` / `to` はスロット列 (集荷 + 経由地) の位置 (0 始まり)。
 */
export function moveVisitSlot(
  pickupSequence: number,
  waypointCount: number,
  from: number,
  to: number,
): { pickupSequence: number; waypointOrder: number[] } {
  const slots = buildVisitSlots(pickupSequence, waypointCount);
  const moved = slots[from];
  if (!moved || to < 0 || to >= slots.length) return readVisitSlots(slots);
  const rest = slots.filter((_, i) => i !== from);
  rest.splice(to, 0, moved);
  return readVisitSlots(rest);
}

/**
 * 経由地の配列インデックス → 注文内の訪問順 (1 始まり)。
 * 集荷と経由地で 1..N の連番を分け合う (contract の検証と同じ規則)。
 */
export function waypointSequences(pickupSequence: number, waypointCount: number): number[] {
  return buildVisitSlots(pickupSequence, waypointCount).flatMap((slot, position) =>
    slot.type === "waypoint" ? [position + 1] : [],
  );
}
